<script context="module">
    import {derived,readable, writable} from 'svelte/store';
    import {session, getJWT} from './Utilities/Session.svelte';

    import { buildLexoRank, NumeralSystem64 } from "@wewatch/lexorank";

    import {notifier} from "@beyonk/svelte-notifications";

    let offline_databases = {};

    export const LexoRank = buildLexoRank({
        NumeralSystem: NumeralSystem64,
        maxOrder: 8,
        initialMinDecimal: "10000",
        defaultGap: "10000"
    })

    // PROD
    //*
    export const db = new PouchDB("katana");
    const DB_HOST = 'https://couch.shuriken.tools'
    const DB_URL = `${DB_HOST}/katana`;

    export const ENV = "prod";


    export const offlineSyncDB = new PouchDB('prod_offline_sync');

    async function dbFetch(url, opts) {
        let jwt = await getJWT();
        opts.headers.set('Authorization', `Bearer ${jwt}`);

        return PouchDB.fetch(url, opts);
    }

    //*/

    // QA
    /*
    export const db = new PouchDB('katana_qa');
    const DB_URL = 'https://couch.shuriken.tools/katana_qa';
    window.db = db;
    //*/


    // BRAZIL
    /*
    export const db = new PouchDB("katana");
    const DB_URL = 'https://br.couch.shuriken.tools/katana';
    //*/

    // DEV
    /*
    export const db = new PouchDB('katana_dev', {auto_compaction: true});
    const DB_HOST = 'https://dev.katana.shuriken.tools'
    const DB_URL = `${DB_HOST}/katana2023`;

    export const offlineSyncDB = new PouchDB('dev_offline_sync');

    async function dbFetch(url, opts) {
        let jwt = await getJWT();
        opts.headers.set('Authorization', `Bearer ${jwt}`);

        return PouchDB.fetch(url, opts);
    }



    export const ENV = "dev";

    window.pdb = db;
    window.LexoRank = LexoRank;
    //*/

    db.createIndex({
        index: {fields: ["type"]}
    });

    const dbput = db.put;

    export const db_exists = readable(false, (set) => {
        db.info().then(details => {
            if (details.doc_count == 0 && details.update_seq == 0) {
                set(false);
            } else {
                set(true);
            }
        })
    })

    let syncHandler;

    export function getAssessmentDB(db_name) {
        let assessment_db;

        if (offline_databases[db_name]) {
            assessment_db = offline_databases[db_name].db;
        } else {
            assessment_db = new PouchDB(`${DB_HOST}/${db_name}`, {
                fetch: dbFetch
            });
        }

        // We should probably catch this somewhere else, but since thie is currently sync, we
        // just show an error.
        assessment_db.info().catch((e) => {
            notifier.danger("You do not have permission to access this assessment!")
        });

        return assessment_db;
    }

    export const syncStatus = derived(session, async (sess, set) => {
        if (syncHandler) {
            syncHandler.cancel();
        }

        if (!sess.loggedIn || sess.workOffline) {
            set(0);
            return;
        }

        // override db.put
        db.put = (doc) => {
            const now = +(Date.now());

            doc.last_update_user = sess.user.email;
            doc.last_update_time = now;
            if (!doc.create_time) {
                doc.create_time = now;
            }

            return dbput(doc);
        }

        let remoteDB = new PouchDB(DB_URL, {
            fetch: (url, opts) => {
                return dbFetch(url, opts).catch(() => {
                    set(0)
                })
            }
        });

        async function setupSync() {
            // console.log("STARTING SYNC")
            set(-1);
            await db.sync(remoteDB, {
                batch_size: 500
            });
            set(1);
            // console.log("DONE SYNCING")

            syncHandler = db.sync(remoteDB, {
                live: true,
                retry: true
            }).on("complete", () =>{
                set(0);
            }).on("error", (err) => {
                console.error('SYNC ERR', err);

                set(0);
            });
        }

        setupSync();

    }, 0);

    let last_sync_status;
    export const offlineDatabases = derived(syncStatus, ($syncStatus, set) => {
        if ($syncStatus != 1) return set({});

        // check if sync status changed
        if (last_sync_status != $syncStatus) {
            last_sync_status = $syncStatus;
        } else {
            return;
        }

        function startSync(db_name) {
            // check if database is already syncing
            if (offline_databases[db_name]) {
                console.log(`Sync for ${db_name} already started`)
                return;
            }

            //console.log(`Starting Sync for ${db_name}`);

            let offline_db = new PouchDB(db_name);
            let remote_db = new PouchDB(`${DB_HOST}/${db_name}`, {
                fetch: dbFetch
            });

            offline_databases[db_name] = {
                db: offline_db,
                syncHandler: null,
                syncStatus: -1
            }

            set(offline_databases);

            offline_db.sync(remote_db).then(() => {
                offline_databases[db_name].syncStatus = 1;
                offline_databases[db_name].syncHandler = offline_db.sync(remote_db, {
                    live: true,
                    retry: true
                }).on("paused", () => {
                    offline_databases[db_name].syncStatus = 0;
                    set(offline_databases);
                }).on("error", () => {
                    offline_databases[db_name].syncStatus = 2;
                    set(offline_databases);
                }).on("denied", () => {
                    offline_databases[db_name].syncStatus = 2;
                    set(offline_databases);
                }).on('active', () => {
                    offline_databases[db_name].syncStatus = 1;
                    set(offline_databases);
                })

                set(offline_databases);
            });
        }

        offlineSyncDB.allDocs().then((res) => {
            for (let row of res.rows) {
                let db_name = row.id;
                startSync(db_name);
            }
        });

        set(offline_databases);

        offlineSyncDB.changes({
            since: 'now',
            live: true,
            include_docs: true
        }).on("change", (change) => {
            let db_name = change.id;
            if (change.deleted) {
                if (offline_databases[db_name].syncHandler) {
                    offline_databases[db_name].syncHandler.cancel();
                }
                offline_databases[db_name].db.destroy();
                delete offline_databases[db_name];
                set(offline_databases);
            } else {
                startSync(db_name);
            }
        })
    })

    export function uuid(timestamp) {
        if (!timestamp) {
            timestamp = Date.now()
        }

        return timestamp.toString(36) + Math.floor(Math.random() * 4228250625).toString(36)
    }
</script>